import React, { FunctionComponent, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { Loader } from "@/components";
import { classNames } from "@/utils/classNames";
import { Radio } from "@/components/Radio/Radio";

import { AutoCompleteItem, AutoComplete } from "@/components/AutoComplete/AutoComplete";

import { LinkedinParams } from "@/pages/Private/pages/LiPeople/components/LinkedinTagAutocomplete";

import { useSearch } from "@/utils";

import {
	useGetSimilarTitlesMutation,
	useLazyGetFilterByPropertyQuery,
} from "@/pages/Private/redux/list/list.api";

import { Checkbox } from "@/components/Checkbox/Checkbox";

import { Information } from "@/components/Information/Information";

import { EnrichmentFilterConfig } from "../../ListEditDetails";
import { IcpPosition } from "../../enum/icp.enum";
import { List } from "../../../../schema/list";

import { SearchEngineBadge } from "./SearchEngineBadge";
import { SEARCH_ENGINE_BY_POSITION } from "./searchProfileEngine";

interface CustomTitleOptionProps {
	list: List;
	titleFilters?: EnrichmentFilterConfig;
	setTitleFilters: React.Dispatch<React.SetStateAction<EnrichmentFilterConfig | undefined>>;
	prio: "first" | "second" | "third";
	error?: boolean;
}

/**
 * The "Search for a different position or job title" option: a free-text title autocomplete with
 * AI similar-title expansion and the decision-makers-only modifier. Owns its own job-title search
 * state so the parent position column stays small.
 */
export const CustomTitleOption: FunctionComponent<CustomTitleOptionProps> = ({
	list,
	titleFilters,
	setTitleFilters,
	prio,
	error,
}) => {
	const {
		query: jobTitleQuery,
		searchValue: jobTitleSearchValue,
		setSearchValue: setJobTitleSearchValue,
	} = useSearch();

	const { t } = useTranslation();
	const ts = (key: string) => t(`defineIcps.defineIcp.${key}`);

	const [fetchTags] = useLazyGetFilterByPropertyQuery();
	const [jobOptions, setJobOptions] = useState<LinkedinParams[]>([]);
	const [getSimilarTitles, { isLoading: isSimilarTitlesLoading }] = useGetSimilarTitlesMutation();

	const handleGetSimilarTitles = async (
		filterTitle: "first" | "second" | "third",
		searchTerm: string,
		decisionMakersOnly: boolean
	) => {
		const response = await getSimilarTitles({
			searchTerm,
			listId: list.id,
			decisionMakersOnly: decisionMakersOnly,
		}).unwrap();

		setTitleFilters({
			...titleFilters,
			[filterTitle]: {
				...titleFilters?.[filterTitle],
				decisionMakersOnly,
				similarTitleSearch: true,
				title: searchTerm,
				similarTitles: response.map((res) => ({
					title: res,
					active: true,
				})),
			},
		});
	};

	useEffect(() => {
		if (jobTitleQuery) {
			fetchTags({ propertyName: "title", searchTerm: jobTitleQuery }).then((results) => {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const optionValues = results?.data?.data?.map((res: any) => {
					return {
						name: res.value,
						id: res.id,
					};
				});

				if (optionValues) {
					setJobOptions([...optionValues]);
				}
			});
		}
	}, [jobTitleQuery, fetchTags]);

	const fetchSimilarTitles = (
		title: string,
		decisionMakersOnly: boolean,
		filterTitle: "first" | "second" | "third"
	) => {
		if (title) {
			handleGetSimilarTitles(filterTitle, title, decisionMakersOnly);
		}
	};

	return (
		<div className="mb-4">
			<Radio
				isChecked={titleFilters?.[prio]?.position === IcpPosition.CUSTOM}
				name="position"
				value={IcpPosition.CUSTOM}
				onChange={() =>
					setTitleFilters({
						...titleFilters,
						[prio]: {
							...titleFilters?.[prio],
							position: IcpPosition.CUSTOM,
						},
					})
				}
			>
				<span className="text-text-meta ml-2 flex items-center gap-1.5">
					{ts("searchForADifferentPositionOrJobTitle")}
					<SearchEngineBadge meta={SEARCH_ENGINE_BY_POSITION[IcpPosition.CUSTOM]} />
				</span>
			</Radio>
			{titleFilters?.[prio]?.position === IcpPosition.CUSTOM && (
				<div className="mt-2 ml-8 flex flex-col items-start">
					<div className="flex flex-row items-center mb-4 self-stretch">
						<AutoComplete
							data={
								[
									{ id: jobTitleSearchValue || "", title: jobTitleSearchValue || "" },
									...(jobOptions
										?.filter((r) => r.name !== jobTitleSearchValue)
										?.map((r) => ({
											id: r.id,
											title: r.name,
										})) || []),
								]?.filter((r) => r.id !== "") || []
							}
							error={error || false}
							handleReset={() => {
								setJobTitleSearchValue("");
								setTitleFilters({
									...(titleFilters || {}),
									[prio]: {
										...(titleFilters?.[prio] || {}),
										similarTitleSearch: false,
										title: undefined,
										similarTitles: [],
										decisionMakersOnly: false,
									},
								});
							}}
							handleSearch={(value: string) => setJobTitleSearchValue(value)}
							handleSelect={async (item?: AutoCompleteItem) => {
								if (item) {
									setTitleFilters({
										...(titleFilters || {}),
										[prio]: {
											...(titleFilters?.[prio] || {}),
											title: item.title,
										},
									});
									setJobTitleSearchValue("");
									if (titleFilters?.[prio]?.similarTitleSearch) {
										fetchSimilarTitles(
											item.title,
											titleFilters?.[prio]?.decisionMakersOnly || false,
											prio
										);
									}
								} else {
									setTitleFilters({
										...(titleFilters || {}),
										[prio]: undefined,
									});
								}
							}}
							placeholder="e.g. Head of Product"
							selectedItem={titleFilters?.[prio]?.title || ""}
						/>
					</div>
					<div className="flex flex-row items-center mb-2">
						<Checkbox
							isChecked={titleFilters?.[prio]?.decisionMakersOnly || false}
							isDisabled={!titleFilters?.[prio]?.title}
							onChange={() => {
								const initialStatus = titleFilters?.[prio]?.decisionMakersOnly;

								setTitleFilters({
									...(titleFilters || {}),
									[prio]: {
										...(titleFilters || {})?.[prio],
										decisionMakersOnly: !initialStatus,
									},
								});
								if (titleFilters?.[prio]?.similarTitleSearch) {
									fetchSimilarTitles(titleFilters?.[prio]?.title || "", !initialStatus, prio);
								}
							}}
						>
							{ts("onlySearchForDecisionMakersHeadOfAndHigher")}
						</Checkbox>
					</div>
					<div className="flex flex-row items-center mb-2">
						<Checkbox
							isChecked={titleFilters?.[prio]?.similarTitleSearch || false}
							isDisabled={!titleFilters?.[prio]?.title}
							onChange={() => {
								const initialStatus = titleFilters?.[prio]?.similarTitleSearch;

								if (!initialStatus) {
									setTitleFilters({
										...(titleFilters || {}),
										[prio]: {
											...(titleFilters || {})?.[prio],
											similarTitleSearch: !initialStatus,
										},
									});
									fetchSimilarTitles(
										titleFilters?.[prio]?.title || "",
										titleFilters?.[prio]?.decisionMakersOnly || false,
										prio
									);
								} else {
									setTitleFilters({
										...(titleFilters || {}),
										[prio]: {
											...(titleFilters || {})?.[prio],
											similarTitleSearch: !initialStatus,
											similarTitles: [],
										},
									});
								}
							}}
						>
							<span className="text-text-meta ml-2 flex gap-1">
								{ts("searchForSimilarJobTitles")}{" "}
								<Information information={ts("searchForSimilarJobTitlesInfo")} />
							</span>
						</Checkbox>
					</div>
					{!isSimilarTitlesLoading &&
						titleFilters?.[prio] &&
						titleFilters?.[prio]?.similarTitles && (
							<div className="mb-2">
								{titleFilters?.[prio]?.similarTitles?.map((title) => (
									<span
										key={title.title}
										className={classNames(
											"cursor-pointer inline-flex text-xs px-2 py-1 rounded-md  mr-2 mb-1",
											title.active ? "bg-blue-action text-white" : "bg-white text-text-meta"
										)}
										onClick={() => {
											setTitleFilters({
												...titleFilters,
												[prio]: {
													...titleFilters?.[prio],
													similarTitles: titleFilters?.[prio]?.similarTitles?.map((t) => ({
														...t,
														active: t.title === title.title ? !t.active : t.active,
													})),
												},
											});
										}}
									>
										{title.title}
									</span>
								))}
							</div>
						)}
					{isSimilarTitlesLoading && (
						<div className="text-xs text-text-meta">
							<Loader />
						</div>
					)}
				</div>
			)}
		</div>
	);
};
